import type { TreeNode } from '../../service/tree.service';

export const IAM_LABEL = 'IAM & Access';
export const ACCESS_LABEL = 'Access';

const ADMIN_ROLES = new Set(['SUPERADMIN', 'ADMIN']);

type Index = Record<number, TreeNode> | undefined | null;

/**
 * What the people page of a node is called. It is "IAM & Access" on the node where the
 * signed-in user's access begins (their top role: no ancestor they reach carries a role), and
 * only when that role is administering (SUPERADMIN or ADMIN). Any other top role (DEV, ANALYST,
 * CISO, VIEWER, custom) sees "Access", as does every level beneath the top node. When no role information is available the label falls
 * back to the level: organisation "IAM & Access", entity and workspace "Access".
 */
export const accessLabel = (
  node: Pick<TreeNode, 'id' | 'kind'> | undefined | null,
  index: Index
): string => {
  if (!node) return ACCESS_LABEL;
  const fallback = node.kind === 'organisation' ? IAM_LABEL : ACCESS_LABEL;
  const self = index?.[node.id];
  if (!index || !self) return fallback;

  const seen = new Set<number>([self.id]);
  let ancestorHoldsRole = false;
  let parent = self.parent_id != null ? index[self.parent_id] : undefined;
  while (parent && !seen.has(parent.id)) {
    seen.add(parent.id);
    if (parent.current_user_role) ancestorHoldsRole = true;
    parent = parent.parent_id != null ? index[parent.parent_id] : undefined;
  }

  if (ancestorHoldsRole) return ACCESS_LABEL;
  if (self.current_user_role) return ADMIN_ROLES.has(self.current_user_role) ? IAM_LABEL : ACCESS_LABEL;
  return fallback;
};
